// All Redux selectors, dispatch calls, and router access for the Misc My Records page live here.
// MiscellaneousMyRecords consumes this hook and never calls useSelector/useDispatch/useLocation
// directly, which keeps the component free of infrastructure and its tests free of Redux mocks.
import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import {
  fetchMiscMyRecordsTemplate,
  fetchMiscMyRecords,
  fetchMiscMyRecordsDrawer,
  setMiscMyRecordsFilter,
  setMiscMyRecordsAdvancedFilter,
  resetMiscMyRecordsFilters,
  setMiscMyRecordsSelectedRecordId,
} from './MiscellaneousMyRecordsActions'

const isValidDateOrEmpty = (val) => {
  if (!val) return true
  return !isNaN(new Date(val).getTime())
}

export const useMiscMyRecords = ({ sublinks }) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const {
    template,
    templateLoading,
    items,
    totalCount,
    loading,
    error,
    filters,
    selectedRecordId,
    drawerData,
    drawerLoading,
  } = useSelector((state) => state.miscMyRecords || {})

  const sourceTypeParam = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location])

  const selectedModuleId = useSelector(
    (state) => state.app.miscRecordsSelectedModules[sourceTypeParam],
  )

  const sublink = useMemo(
    () => (sublinks || []).find((x) => x.parameter === sourceTypeParam) || {},
    [sublinks, sourceTypeParam],
  )

  const modules = useMemo(
    () => (sublink.MiscRecordsModules || []).filter((m) => m.DisplayInMyRecords),
    [sublink],
  )

  const selectedModule = useMemo(
    () => modules.find((m) => m.MiscellaneousModuleId == selectedModuleId) || null,
    [modules, selectedModuleId],
  )

  const selectedRecord = useMemo(() => {
    if (!selectedRecordId || !items) return null
    return items.find((r) => r.RecordId === selectedRecordId) || null
  }, [selectedRecordId, items])

  useEffect(() => {
    if (selectedModuleId) {
      dispatch(resetMiscMyRecordsFilters())
      dispatch(fetchMiscMyRecordsTemplate(selectedModuleId))
      dispatch(fetchMiscMyRecords(selectedModuleId))
    }
  }, [selectedModuleId])

  const search = (searchInput) => {
    if (!selectedModuleId) return
    dispatch(setMiscMyRecordsFilter('search', searchInput))
    dispatch(fetchMiscMyRecords(selectedModuleId))
  }

  // Callers that need to reset local state (searchInput, filterOpen, popoutFilters) do so themselves.
  const clearFilters = () => {
    dispatch(resetMiscMyRecordsFilters())
    if (selectedModuleId) {
      dispatch(fetchMiscMyRecords(selectedModuleId))
    }
  }

  const applyFilters = (popoutFilters) => {
    const regStart = isValidDateOrEmpty(popoutFilters.registrationDateStart)
      ? popoutFilters.registrationDateStart
      : null
    const regEnd = isValidDateOrEmpty(popoutFilters.registrationDateEnd)
      ? popoutFilters.registrationDateEnd
      : null
    dispatch(setMiscMyRecordsFilter('registrationDateStart', regStart))
    dispatch(setMiscMyRecordsFilter('registrationDateEnd', regEnd))

    const existingKeys = Object.keys((filters && filters.advancedFilters) || {})
    const popoutKeys = Object.keys(popoutFilters.advancedFilters)
    const allKeys = existingKeys.concat(popoutKeys.filter((k) => existingKeys.indexOf(k) === -1))
    allKeys.forEach((key) => {
      dispatch(setMiscMyRecordsAdvancedFilter(key, popoutFilters.advancedFilters[key] || ''))
    })

    if (selectedModuleId) {
      dispatch(fetchMiscMyRecords(selectedModuleId))
    }
  }

  // Callers that also need to clear the searchInput local state do so themselves when name === 'search'.
  const removeChip = (name) => {
    if (name === 'search') {
      dispatch(setMiscMyRecordsFilter('search', ''))
    } else if (name === 'registrationDate') {
      dispatch(setMiscMyRecordsFilter('registrationDateStart', null))
      dispatch(setMiscMyRecordsFilter('registrationDateEnd', null))
    } else if (name.indexOf('advanced__') === 0) {
      const fieldName = name.slice('advanced__'.length)
      dispatch(setMiscMyRecordsAdvancedFilter(fieldName, ''))
    }
    if (selectedModuleId) {
      dispatch(fetchMiscMyRecords(selectedModuleId))
    }
  }

  const handleRowClick = (row) => {
    const nextId = selectedRecordId === row.RecordId ? null : row.RecordId
    dispatch(setMiscMyRecordsSelectedRecordId(nextId))
    if (nextId !== null) {
      dispatch(fetchMiscMyRecordsDrawer(nextId))
    }
  }

  const handleViewFiling = (row) => {
    dispatch(setMiscMyRecordsSelectedRecordId(row.RecordId))
    dispatch(fetchMiscMyRecordsDrawer(row.RecordId))
  }

  const dismissDrawer = () => {
    dispatch(setMiscMyRecordsSelectedRecordId(null))
  }

  const filterChips = useMemo(() => {
    const chips = []
    if (filters && filters.search) {
      chips.push({ name: 'search', label: 'Search', value: filters.search })
    }
    if (filters && (filters.registrationDateStart || filters.registrationDateEnd)) {
      const start = filters.registrationDateStart
        ? new Date(filters.registrationDateStart).toLocaleDateString()
        : ''
      const end = filters.registrationDateEnd
        ? new Date(filters.registrationDateEnd).toLocaleDateString()
        : ''
      const dateValue =
        start && end ? start + ' – ' + end : start ? 'From ' + start : 'To ' + end
      chips.push({ name: 'registrationDate', label: 'Registration Date', value: dateValue })
    }
    if (filters && filters.advancedFilters) {
      const searchableFields = (template && template.SearchableFields) || []
      Object.keys(filters.advancedFilters).forEach((k) => {
        const val = filters.advancedFilters[k]
        if (val && k.indexOf('__comparator') === -1) {
          const field = searchableFields.find((f) => f.FieldName === k)
          const label = (field && field.DisplayName) || k
          let displayValue = String(val)
          if (field && field.DataType === 'Bool') {
            displayValue = val === 'true' ? 'Yes' : 'No'
          } else if (field && field.DataType === 'LookupType' && field.LookupTypeValuesJson) {
            try {
              const lookupOptions = JSON.parse(field.LookupTypeValuesJson)
              const option =
                Array.isArray(lookupOptions) &&
                lookupOptions.find((item) => String(item.value) === String(val))
              if (option) {
                displayValue = option.label
              }
            } catch (e) {}
          }
          chips.push({ name: 'advanced__' + k, label: label, value: displayValue })
        }
      })
    }
    return chips
  }, [filters, template])

  return {
    template,
    templateLoading,
    items,
    totalCount,
    loading,
    error,
    filters,
    selectedRecordId,
    drawerData,
    drawerLoading,
    selectedModuleId,
    sourceTypeParam,
    sublink,
    modules,
    selectedModule,
    selectedRecord,
    filterChips,
    search,
    clearFilters,
    applyFilters,
    removeChip,
    handleRowClick,
    handleViewFiling,
    dismissDrawer,
  }
}
